import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Spell } from '../../types/character';
import ContentText from '../Common/ContentText';
import './DomainCard.css';

interface DomainCardProps {
  item: Spell;
  defaultExpanded?: boolean;
}

const DomainCard: React.FC<DomainCardProps> = ({ item, defaultExpanded = true }) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  
  // Get the actual type from the domain card data
  const typeLabel = (item as any).originalType || item.type || 'Unknown';
  
  // Get level and domain info
  const level = (item as any).level;
  const domain = (item as any).domain || item.domain;
  
  // Capitalize first letter of type for display
  const displayType = typeLabel.charAt(0).toUpperCase() + typeLabel.slice(1);
  

  return (
    <motion.div
      className="domain-card"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{
        opacity: 1,
        scale: 1,
        boxShadow: 'var(--semantic-shadows-low)'
      }}
      transition={{ duration: 0.2 }}
    >
      <motion.div 
        className="domain-card__header"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        onClick={() => setIsExpanded(!isExpanded)}
        style={{ cursor: 'pointer' }}
      >
        <h4 className="domain-card__name">{item.name}</h4>
        <div className="domain-card__meta">
          <motion.div
            className="domain-card__type"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.15 }}
          >
            <span className="domain-card-type">{displayType}</span>
            {level && (
              <span className="domain-card__level">
                Level {level}
              </span>
            )}
            {domain && (
              <span className="domain-card__domain">
                {domain}
              </span>
            )}
          </motion.div>
        </div>
      </motion.div>

      <AnimatePresence>
        {isExpanded && (
          <motion.div 
            className="domain-card__content"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
          >
            <motion.div 
              className="domain-card__description"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <ContentText content={item.description} />
            </motion.div>

            {/* Show duration information for spells */}
            {typeLabel === 'spell' && item.duration && (
              <motion.div 
                className="domain-card__duration"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <span className="duration-indicator">
                  ⏱️ Duration: {item.duration.type === 'permanent' ? 'Permanent' : 
                             item.duration.type === 'combat' ? 'Until end of combat' :
                             `${item.duration.value || 1} ${item.duration.type}`}
                </span>
              </motion.div>
            )}
            
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default DomainCard;